// frontend/src/components/NotificationBell.jsx
/**
 * NotificationBell — real-time notification badge + dropdown (P12-G10b / P12-G11).
 *
 * Transport
 * ---------
 * Connects to ``WS /api/ws/notifications?token=<JWT>`` on mount.  The backend
 * pushes ``{"type":"unread_count","count":N}`` after every new notification,
 * so the badge stays fresh without REST polling.
 *
 * Graceful degradation
 * --------------------
 * If the WebSocket connection fails (Redis down, token invalid) the component
 * falls back to a 30-second REST polling interval so the badge still updates.
 *
 * Dropdown
 * --------
 * Clicking the bell fetches the 20 most-recent unread notifications via
 * ``GET /api/notifications`` and renders them in a simple list.
 * - Body text is truncated to 80 characters.
 * - Relative timestamps ("2 min ago", "3 hr ago", "5 days ago").
 * - Clicking a row marks it read and navigates to ``action_url`` (if set).
 * - "✕" dismiss button marks a single item read without navigating.
 * - "Mark all read" clears the entire list.
 */

import React, {
  useCallback,
  useEffect,
  useRef,
  useState,
} from "react";
import { useNavigate } from "react-router-dom";
import { useAuth } from "../context/AuthContext";
import client from "../api/client";

// ─── Constants ────────────────────────────────────────────────────────────── //

/** Fallback REST polling interval (ms) when WebSocket is unavailable. */
const FALLBACK_POLL_MS = 30_000;

/** Maximum number of unread notifications shown in the dropdown. */
const MAX_DROPDOWN = 20;

/** Bell SVG path (Heroicons outline bell). */
const BELL_PATH =
  "M15 17h5l-1.405-1.405A2.032 2.032 0 0118 14.158V11a6.002 6.002 0 " +
  "00-4-5.659V5a2 2 0 10-4 0v.341C7.67 6.165 6 8.388 6 11v3.159c0 " +
  ".538-.214 1.055-.595 1.436L4 17h5m6 0v1a3 3 0 11-6 0v-1m6 0H9";

// ─── Helpers ──────────────────────────────────────────────────────────────── //

/**
 * Build the WebSocket URL for the current page's host.
 * Works for both http (→ ws) and https (→ wss).
 */
function wsUrl(token) {
  const proto = window.location.protocol === "https:" ? "wss" : "ws";
  return `${proto}://${window.location.host}/api/ws/notifications?token=${encodeURIComponent(token)}`;
}

/**
 * Human-readable relative-time label, e.g. "just now", "2 min ago", "3 hr ago",
 * "5 days ago".  Matches the format described in the P12-G11 spec.
 */
function relativeTime(isoString) {
  if (!isoString) return "";
  const delta = (Date.now() - new Date(isoString).getTime()) / 1000;
  if (delta < 60) return "just now";
  if (delta < 3600) {
    const m = Math.floor(delta / 60);
    return `${m} min ago`;
  }
  if (delta < 86400) {
    const h = Math.floor(delta / 3600);
    return `${h} hr ago`;
  }
  const d = Math.floor(delta / 86400);
  return `${d} ${d === 1 ? "day" : "days"} ago`;
}

/**
 * Truncate ``text`` to at most ``max`` characters, appending an ellipsis when
 * truncated.  The spec requires bodies to be capped at 80 chars.
 */
function truncate(text, max = 80) {
  if (!text) return "";
  return text.length > max ? `${text.slice(0, max)}…` : text;
}

// ─── Component ────────────────────────────────────────────────────────────── //

/**
 * Notification bell icon with live unread badge and a dropdown list.
 *
 * Mount this inside the topbar of Layout.jsx.
 */
export default function NotificationBell() {
  const { token } = useAuth();
  const navigate = useNavigate();

  // ── State ──────────────────────────────────────────────────────────────── //
  const [unreadCount, setUnreadCount] = useState(0);
  const [open, setOpen] = useState(false);
  const [notifications, setNotifications] = useState([]);
  const [loadingList, setLoadingList] = useState(false);

  // ── Refs ───────────────────────────────────────────────────────────────── //
  const wsRef = useRef(null);
  const fallbackRef = useRef(null);
  const dropdownRef = useRef(null);

  // ── REST helpers ───────────────────────────────────────────────────────── //

  const fetchCount = useCallback(async () => {
    try {
      const { data } = await client.get("/api/notifications/count");
      setUnreadCount(data.count ?? 0);
    } catch {
      // Silently swallow network errors — badge just doesn't update.
    }
  }, []);

  const fetchNotifications = useCallback(async () => {
    setLoadingList(true);
    try {
      const { data } = await client.get("/api/notifications", {
        params: { limit: MAX_DROPDOWN },
      });
      setNotifications(data.notifications ?? data ?? []);
    } catch {
      // noop
    } finally {
      setLoadingList(false);
    }
  }, []);

  const markRead = useCallback(async (id) => {
    try {
      await client.post(`/api/notifications/${id}/read`);
      setNotifications((prev) => prev.filter((n) => n.id !== id));
      setUnreadCount((c) => Math.max(0, c - 1));
    } catch {
      // noop
    }
  }, []);

  const markAllRead = useCallback(async () => {
    try {
      await client.post("/api/notifications/read-all");
      setNotifications([]);
      setUnreadCount(0);
    } catch {
      // noop
    }
  }, []);

  /**
   * Handle a click on a notification row.
   *
   * Marks the notification as read, closes the dropdown, and — if the
   * notification carries an ``action_url`` — navigates there.  Internal
   * paths (starting with ``/``) use React Router so the SPA doesn't reload;
   * external URLs open in a new tab.
   */
  const handleNotificationClick = useCallback(
    (n) => {
      markRead(n.id);
      setOpen(false);
      if (n.action_url) {
        if (n.action_url.startsWith("/")) {
          navigate(n.action_url);
        } else {
          window.open(n.action_url, "_blank", "noopener,noreferrer");
        }
      }
    },
    [markRead, navigate],
  );

  // ── WebSocket setup ────────────────────────────────────────────────────── //

  const startFallbackPoll = useCallback(() => {
    if (fallbackRef.current) return; // already running
    fetchCount();
    fallbackRef.current = setInterval(fetchCount, FALLBACK_POLL_MS);
  }, [fetchCount]);

  const stopFallbackPoll = useCallback(() => {
    if (fallbackRef.current) {
      clearInterval(fallbackRef.current);
      fallbackRef.current = null;
    }
  }, []);

  useEffect(() => {
    if (!token) return;

    let ws;
    let dead = false; // guard against setState after unmount

    function connect() {
      try {
        ws = new WebSocket(wsUrl(token));
        wsRef.current = ws;
      } catch {
        startFallbackPoll();
        return;
      }

      ws.onopen = () => {
        stopFallbackPoll();
      };

      ws.onmessage = (evt) => {
        if (dead) return;
        try {
          const msg = JSON.parse(evt.data);
          if (msg.type === "unread_count") {
            setUnreadCount(msg.count);
          }
        } catch {
          // ignore malformed frames
        }
      };

      ws.onerror = () => {
        // onerror is always followed by onclose
      };

      ws.onclose = () => {
        if (dead) return;
        wsRef.current = null;
        startFallbackPoll();
      };
    }

    connect();

    return () => {
      dead = true;
      stopFallbackPoll();
      if (ws) {
        ws.onclose = null; // prevent the fallback from starting on intentional close
        ws.close();
      }
    };
  }, [token, startFallbackPoll, stopFallbackPoll]);

  // ── Dropdown open/close ────────────────────────────────────────────────── //

  useEffect(() => {
    if (!open) return;
    fetchNotifications();
  }, [open, fetchNotifications]);

  // Close dropdown on outside click.
  useEffect(() => {
    if (!open) return;
    function handleClick(e) {
      if (dropdownRef.current && !dropdownRef.current.contains(e.target)) {
        setOpen(false);
      }
    }
    document.addEventListener("mousedown", handleClick);
    return () => document.removeEventListener("mousedown", handleClick);
  }, [open]);

  // ── Render ─────────────────────────────────────────────────────────────── //

  return (
    <div className="relative" ref={dropdownRef}>
      {/* Bell button */}
      <button
        type="button"
        onClick={() => setOpen((v) => !v)}
        aria-label={`Notifications${unreadCount > 0 ? ` (${unreadCount} unread)` : ""}`}
        className="relative p-1.5 text-gray-500 hover:text-gray-800 rounded-full hover:bg-gray-100 transition-colors focus:outline-none focus-visible:ring-2 focus-visible:ring-blue-500"
      >
        <svg
          viewBox="0 0 24 24"
          fill="none"
          stroke="currentColor"
          strokeWidth={1.8}
          className="w-5 h-5"
        >
          <path strokeLinecap="round" strokeLinejoin="round" d={BELL_PATH} />
        </svg>

        {/* Unread badge */}
        {unreadCount > 0 && (
          <span
            aria-hidden="true"
            className="absolute -top-0.5 -right-0.5 flex items-center justify-center min-w-[16px] h-4 px-0.5 rounded-full bg-red-500 text-white text-[10px] font-bold leading-none"
          >
            {unreadCount > 99 ? "99+" : unreadCount}
          </span>
        )}
      </button>

      {/* Dropdown panel */}
      {open && (
        <div className="absolute right-0 mt-2 w-80 bg-white rounded-xl shadow-lg border border-gray-200 z-50 overflow-hidden">
          {/* Header */}
          <div className="flex items-center justify-between px-4 py-3 border-b border-gray-100">
            <span className="text-sm font-semibold text-gray-800">
              Notifications
            </span>
            {notifications.length > 0 && (
              <button
                type="button"
                onClick={markAllRead}
                className="text-xs text-blue-600 hover:text-blue-800 transition-colors"
              >
                Mark all read
              </button>
            )}
          </div>

          {/* List */}
          <ul className="max-h-80 overflow-y-auto divide-y divide-gray-50">
            {loadingList ? (
              <li className="px-4 py-6 text-sm text-gray-400 text-center">
                Loading…
              </li>
            ) : notifications.length === 0 ? (
              <li className="px-4 py-6 text-sm text-gray-400 text-center">
                You're all caught up 🎉
              </li>
            ) : (
              notifications.map((n) => (
                <li key={n.id} className="group">
                  {/*
                   * The entire row is a button so keyboard and pointer users
                   * can activate it.  The dismiss (✕) button stops propagation
                   * so it only marks-read without navigating.
                   */}
                  <div
                    role="button"
                    tabIndex={0}
                    onClick={() => handleNotificationClick(n)}
                    onKeyDown={(e) => {
                      if (e.key === "Enter" || e.key === " ") {
                        e.preventDefault();
                        handleNotificationClick(n);
                      }
                    }}
                    className={`flex items-start gap-3 px-4 py-3 transition-colors cursor-pointer hover:bg-gray-50 focus:outline-none focus-visible:bg-blue-50 ${
                      n.action_url ? "cursor-pointer" : "cursor-default"
                    }`}
                  >
                    <div className="flex-1 min-w-0">
                      <p className="text-sm font-medium text-gray-800 truncate">
                        {n.title}
                      </p>
                      {/* Body capped at 80 chars per spec */}
                      <p className="text-xs text-gray-500 mt-0.5">
                        {truncate(n.body, 80)}
                      </p>
                      <p className="text-[11px] text-gray-400 mt-1">
                        {relativeTime(n.created_at)}
                      </p>
                    </div>
                    {/* Dismiss button — marks read without navigating */}
                    <button
                      type="button"
                      onClick={(e) => {
                        e.stopPropagation();
                        markRead(n.id);
                      }}
                      aria-label="Dismiss notification"
                      className="mt-0.5 shrink-0 text-gray-300 hover:text-gray-600 opacity-0 group-hover:opacity-100 transition-opacity focus:opacity-100"
                      title="Mark as read"
                    >
                      <svg
                        viewBox="0 0 20 20"
                        fill="currentColor"
                        className="w-4 h-4"
                      >
                        <path
                          fillRule="evenodd"
                          d="M4.293 4.293a1 1 0 011.414 0L10 8.586l4.293-4.293a1 1 0 111.414 1.414L11.414 10l4.293 4.293a1 1 0 01-1.414 1.414L10 11.414l-4.293 4.293a1 1 0 01-1.414-1.414L8.586 10 4.293 5.707a1 1 0 010-1.414z"
                          clipRule="evenodd"
                        />
                      </svg>
                    </button>
                  </div>
                </li>
              ))
            )}
          </ul>
        </div>
      )}
    </div>
  );
}
